import { type ItineraryService } from "../services/ItineraryService";
import { type PricingPrefs } from "../store/persistence";
import { type ItineraryDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { type PricingEvaluationResponseDTO } from "../types/dtos/api/pricing/pricing-evaluation.dto";

/** Subset of pricing prefs that affect CFF segmentation. */
export type CffPricingPrefsSnapshot = Pick<
    PricingPrefs,
    "swissReductionCard" | "swissSuperSaver" | "categoryOfService"
>;

/**
 * Captures the CFF-related pricing preferences that influence segmentation.
 *
 * @param prefs Persisted pricing preferences from `pricingPrefsSlice`.
 * @returns Snapshot used to detect changes when the pricing modal closes.
 */
export const captureCffPricingPrefsSnapshot = ( prefs: PricingPrefs ): CffPricingPrefsSnapshot => {
    return {
        "swissReductionCard": prefs.swissReductionCard,
        "swissSuperSaver":    prefs.swissSuperSaver,
        "categoryOfService":  prefs.categoryOfService,
    };
};

/**
 * Returns whether CFF pricing preferences changed between two snapshots.
 *
 * @param before Snapshot captured when the pricing modal opened.
 * @param after Snapshot read when the pricing modal closed.
 * @returns `true` when any CFF segmentation input changed.
 */
export const haveCffPricingPrefsChanged = (
    before: CffPricingPrefsSnapshot,
    after: CffPricingPrefsSnapshot,
): boolean => {
    return before.swissReductionCard !== after.swissReductionCard ||
      before.swissSuperSaver !== after.swissSuperSaver ||
      before.categoryOfService !== after.categoryOfService;
};

/** Inputs required to re-run list pricing evaluation. */
export interface ReEvaluateItinerariesListPricingParams {
    "itineraryService":   ItineraryService;
    "itineraries":        readonly ItineraryDTO[];
    "pricingPreferences": ItineraryPricingPreferencesDTO;
    "signal"?:            AbortSignal;
}

/**
 * Re-evaluates pricing and segmentation for all itineraries in the current list.
 *
 * @param params Itinerary service, list payload, preferences, and optional abort signal.
 * @returns Pricing evaluation response from `POST /api/pricing/evaluation`.
 */
export const reEvaluateItinerariesListPricing = async( params: ReEvaluateItinerariesListPricingParams ): Promise<PricingEvaluationResponseDTO> => {
    const itinerariesById = Object.fromEntries( params.itineraries.map( ( itinerary ) => [ itinerary.id, itinerary ] ) );

    return params.itineraryService.evaluateItinerariesPricing(
        itinerariesById,
        params.pricingPreferences,
        params.signal,
    );
};
